'use client'

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useOtmsService from '../../hooks/useOtmsService';

const UserCardSimple = ({ personId, groupInfo, onUserClick, onGroupClick }) => {
  const { personnel } = useOtmsService();
  const [userData, setUserData] = useState(null);
  const [positionData, setPositionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [userResponse, positionsResponse] = await Promise.all([
          personnel.getPersonById(personId),
          personnel.getPrimaryPosition(personId).catch(() => null) // Catch error and return null
        ]);

        if (userResponse?.data) {
          setUserData({
            name: userResponse.data.fullName,
            imageUrl: userResponse.data.imageUrl,
          });
        }

        if (positionsResponse?.data) {
          setPositionData({
            position: positionsResponse.data.position.title,
          });
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (personId) {
      fetchData();
    }
  }, [personId]);

  const handleGroupClick = (e) => {
    if (groupInfo?.id) {
      if (onGroupClick) {
        onGroupClick(e);
      } else {
        navigate(`/engage/groups/${groupInfo.id}`);
      }
    }
  };

  const handleUserClick = (e) => {
    if (personId && onUserClick) {
      onUserClick(e);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center p-2">
        <div className="animate-pulse flex space-x-2">
          <div className="rounded-full bg-gray-200 h-8 w-8"></div>
          <div className="flex-1 space-y-1">
            <div className="h-3 bg-gray-200 rounded"></div>
            <div className="h-2 bg-gray-200 rounded w-3/4"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!userData) return null;

  return (
    <div className="flex items-center gap-2">
      {userData.imageUrl ? (
        <img
          src={userData.imageUrl}
          alt={userData.name}
          className="h-8 w-8 rounded-full cursor-pointer"
          onClick={handleUserClick}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = `data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23666"><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-size="24">${userData.name?.charAt(0) || '?'}</text></svg>`;
          }}
        />
      ) : (
        <span 
          className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-gray-500 cursor-pointer"
          onClick={handleUserClick}
        >
          <span className="text-sm font-medium leading-none text-white">
            {userData.name?.charAt(0) || '?'}
          </span>
        </span>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center space-x-1">
          <h3 
            className="text-sm font-medium text-gray-900 truncate cursor-pointer hover:text-blue-600"
            onClick={handleUserClick}
          >
            {userData.name}
          </h3>
          {groupInfo && (
            <>
              <span className="text-xs text-gray-500">on</span>
              <button
                onClick={handleGroupClick}
                className="text-sm font-medium text-gray-900 hover:text-blue-600 truncate"
              >
                {groupInfo.name}
              </button>
            </>
          )}
        </div>
        {positionData?.position && (
          <p className="text-xs text-gray-500 truncate">{positionData.position}</p>
        )}
      </div>
    </div>
  );
};

export default UserCardSimple; 